import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useAverageProductionAmount } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { MessagingOverview, RoiBaseFilters } from '../../types';
import { StatItem, type StatItemData } from '../stat-item';
import { AppointmentScheduledChart } from './charts/appointments-scheduled-chart';
import { MessagesSentChart } from './charts/message-sent-chart';
import { RevenueChart } from './charts/revenue-chart';

interface MessagingSubViewProps {
  dateRangeHelperText?: string;
  filters?: RoiBaseFilters;
  isDemoAccount?: boolean;
  isLoading?: boolean;
  overviewData: MessagingOverview;
  subViewUrl: string;
}

export const MessagingSingleSubView = ({
  dateRangeHelperText,
  filters,
  isDemoAccount,
  isLoading,
  overviewData,
  subViewUrl,
}: MessagingSubViewProps) => {
  const { t } = useTranslation('analytics');
  const search = useSearch({ strict: false }) ?? { view: 'leadsContacted' };
  const { averageProductionAmount } = useAverageProductionAmount();

  const stats: StatItemData[] = useMemo(() => {
    switch (search.view) {
      case 'leadsContacted':
        return [
          {
            label: t('Messages Sent'),
            value: formatters.value(overviewData.leadsContacted.messagesSent),
          },
          {
            label: t('Leads Contacted'),
            value: formatters.value(overviewData.leadsContacted.totalPatients),
          },
        ];

      case 'patientsScheduled':
        return [
          {
            label: t('Appointments Scheduled'),
            value: formatters.value(overviewData.patientsScheduled.appointmentsScheduledCount),
          },
          {
            label: t('Patients'),
            value: formatters.value(overviewData.patientsScheduled.totalPatients),
          },
        ];

      case 'revenueGenerated':
        return [
          {
            label: t('Revenue Generated'),
            value: formatters.shortenCurrency(
              overviewData.revenueGenerated.totalCompletedAppts * averageProductionAmount.value
            ),
          },
          {
            label: t('Appointments Completed'),
            value: formatters.value(overviewData.revenueGenerated.totalCompletedAppts),
          },
        ];
    }

    return [];
  }, [overviewData, search.view, t, averageProductionAmount]);

  const commonProps = {
    dateRangeHelperText,
    filteredLocationIds: filters?.locationIds ?? [],
    isDemoAccount,
    isLoading,
    subViewUrl,
  };

  return (
    <div css={styles.chartContainer}>
      {stats.map((props) => (
        <StatItem {...props} key={props.label} subAlignment='bottom' valueFontSize={theme.fontSize(66)} />
      ))}

      <div>
        {search.view === 'leadsContacted' ? (
          <>
            <Text css={styles.analysisTitle} size='large' textAlign='center'>
              {t('Leads Contacted Analysis')}
            </Text>
            <MessagesSentChart {...commonProps} data={overviewData.leadsContacted} />
          </>
        ) : search.view === 'patientsScheduled' ? (
          <>
            <Text css={styles.analysisTitle} size='large' textAlign='center'>
              {t('Patients Appointments Scheduled Analysis')}
            </Text>
            <AppointmentScheduledChart {...commonProps} data={overviewData.patientsScheduled} isLoading={isLoading} />
          </>
        ) : (
          <>
            <Text css={styles.analysisTitle} size='large' textAlign='center'>
              {t('Revenue Generated Analysis')}
            </Text>
            <RevenueChart {...commonProps} data={overviewData.revenueGenerated} isLoading={isLoading} />
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  chartContainer: css`
    align-items: center;
    column-gap: ${theme.spacing(10)};
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    padding: ${theme.spacing(5)};
    row-gap: ${theme.spacing(5)};

    @container (min-width: 980px) {
      padding: 0;
    }
  `,

  analysisTitle: css`
    font-style: italic;
    margin-bottom: ${theme.spacing(2)};
  `,
};
